import { ActivityMappedData, ActivityVariant } from "../../../Components/Activity/activity";
import { routePaths } from "../../../Router/router";
import { GetAssetObjectReadableType } from "../../../Utils/helpers";
import ApiClient from "../api-client";
import { cacheService, EnvType } from "../services";
import { ActivityKind } from "../Types/activity-kind";
import { ActivityType } from "../Types/activity-type";
import { CreateActivityRequest } from "../Types/create-activity-request";
import { CreateActivityResponse } from "../Types/create-activity-response";
import { DbActivityInfos } from "../Types/db-activity-infos";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetActivitiesByTypesRequest } from "../Types/get-activities-by-types-request";
import { GetActivitiesResponse } from "../Types/get-activities-response";
import { GetActivityEnumsResponse } from "../Types/get-activity-enums-response";
import { GetUserActivitiesCountResponse } from "../Types/get-user-activities-count-response";
import { ObjectLayer } from "../Types/object-layer";
import { ObjectType } from "../Types/object-type";
import { default as ILogApiClient, default as LogApiClient } from "./log-api-client";

export default class LogService {
  logApiClient: ILogApiClient;

  constructor(envType: EnvType) {
    this.logApiClient = new LogApiClient(new ApiClient(envType), envType);
  }
  async ListSessionUserActivities(pageNb: number, pageSize: number): Promise<ActivityMappedData[]> {
    const response = await this.logApiClient.ListSessionUserActivities(pageNb, pageSize);

    return this.GetActivityPropsFromGetActivitiesResponse(response);
  }
  async ListSessionUserActivitiesByTypes(request: GetActivitiesByTypesRequest): Promise<ActivityMappedData[]> {
    const response = await this.logApiClient.ListSessionUserActivitiesByTypes(request);

    return this.GetActivityPropsFromGetActivitiesResponse(response);
  }

  public GetMappedActivity(request: DbActivityInfos): ActivityMappedData {
    return this.MapActivityData(request, cacheService.GetUserInfo()!.id);
  }

  async ListResourceActivities(
    resourceKind: ActivityKind,
    resourceId: number,
    pageNb: number,
    pageSize: number
  ): Promise<ActivityMappedData[]> {
    const response = await this.logApiClient.ListResourceActivities(resourceKind, resourceId, pageNb, pageSize);
    return this.GetActivityPropsFromGetActivitiesResponse(response);
  }

  async ReadAllActivities(): Promise<void> {
    await this.logApiClient.ReadAllActivities(0);
  }

  // not used atm
  async ListAllActivitiesByPeriod(beginAsString: string, endAsString: string): Promise<GetActivitiesResponse> {
    return await this.logApiClient.ListAllActivitiesByPeriod(beginAsString, endAsString);
  }
  async ListAllActivitiesByPage(pageNb: number, pageSize: number): Promise<GetActivitiesResponse> {
    return await this.logApiClient.ListAllActivitiesByPage(pageNb, pageSize);
  }
  async ListAllUserActivities(userId: number): Promise<GetActivitiesResponse> {
    return await this.logApiClient.ListAllUserActivities(userId);
  }
  async ListAllUserActivitiesByPeriod(userId: number, dateIn: string, dateOut: string): Promise<GetActivitiesResponse> {
    return await this.logApiClient.ListAllUserActivitiesByPeriod(userId, dateIn, dateOut);
  }
  async ReadActivity(id: number): Promise<GenericResponseStd> {
    return await this.logApiClient.ReadActivity(id);
  }
  async SolveActivity(id: number): Promise<GenericResponseStd> {
    return await this.logApiClient.SolveActivity(id);
  }
  async CountUserActivities(userId: number): Promise<GetUserActivitiesCountResponse> {
    return await this.logApiClient.CountUserActivities(userId);
  }
  async GetUserActionsEnums(): Promise<GetActivityEnumsResponse> {
    return await this.logApiClient.GetUserActionsEnums();
  }
  async InsertActivityComment(request: CreateActivityRequest): Promise<CreateActivityResponse> {
    return await this.logApiClient.InsertActivityComment(request);
  }

  //------private------

  private GetActivityPropsFromGetActivitiesResponse(response: GetActivitiesResponse): ActivityMappedData[] {
    // if array is empty, return empty array
    if (response.activities === null || response.activities.length === 0) return [];
    let mappedData: ActivityMappedData[] = [];
    const loggedInUserId = cacheService.GetUserInfo()!.id;

    // loop over activities
    response.activities
      .filter((x) => !x.replyId)
      .forEach((activity) => {
        let act = this.MapActivityData(activity, loggedInUserId);
        let replies = response.activities?.filter((x) => x.replyId && x.replyId === activity.id);
        let repliesMapped: ActivityMappedData[] = [];

        replies?.forEach((x) => {
          repliesMapped.push(this.MapActivityData(x, loggedInUserId));
        });

        act.replies = repliesMapped;

        mappedData.push(act);
      });
    return mappedData;
  }

  private MapActivityData = (activity: DbActivityInfos, loggedInUserId: number): ActivityMappedData => {
    // get props that need compute
    const activityVariant = activity.type
      ? this.GetActivityVariantFromActivityType(activity.type, activity.solved !== null)
      : "secondary";
    // TODO : remove || "Not Defined" and "| null = null" from property on object AFTER merge of new activities
    const activityUserName =
      (activity.userId === null ? "System" : activity.userId !== loggedInUserId ? activity.userName : "You") ||
      "Not Defined";
    const [headingAction, headingLabel] = this.GetHeadingActionAndLabelFromTypExp(activity.typeExp || "");
    const userInitials = this.GetUserInitialsFromUserName(activityUserName);
    const contextLabel = this.GetContextLabelFromObjectType(activity.kind, activity.assetObjectType);
    const headingResourceLink = this.GetResourceLink(activity.kind, activity.type, activity.resId);

    return {
      id: activity.id,
      replyId: activity.replyId,
      // user infos
      userName: activityUserName,
      userId: activity.userId,
      userInitials: userInitials,
      // activity
      activityKind: activity.kind,
      activityType: activity.type,
      activityDate: activity.date || undefined,
      activityVariant: activityVariant,
      error: activityVariant === "error" && !activity.solved,
      read: activity.isRead,
      solved: activity.solved,
      activityDetails: activity.msg || "",
      // heading
      headingLabel: headingLabel,
      headingAction: headingAction,
      headingLinkToResource: headingResourceLink,
      // context
      contextAssetType: activity.assetObjectType || undefined,
      contextAssetLayer: activity.assetObjectLayer || undefined,
      contextTitle: activity.objName,
      contextSubtitle: undefined,
      contextLabel: contextLabel
        ? contextLabel + (activity.solved !== null ? "Solved" : "")
        : activity.solved !== null
        ? "Solved"
        : undefined,
      contextResId: activity.resId,
      replies: [],
    };
  };

  private GetDataForTest(): GetActivitiesResponse {
    const response = new GetActivitiesResponse();
    response.activities = [];

    // Asset title create by you
    response.activities.push(
      new DbActivityInfos({
        id: 0,
        platformId: 1,
        userId: 2,
        resId: 0,
        kind: ActivityKind.OrderItem,
        type: ActivityType.OrderPlay,
        typeExp: "played an order",
        date: new Date(2022, 10, 7, 10, 28),
        msg: "OrderItem details",
        objName: "Order asset name",
        assetObjectType: undefined,
        assetObjectLayer: undefined,
        userName: "Adrien Corbin",
        isRead: false,
      })
    );

    // Asset title create by you
    response.activities.push(
      new DbActivityInfos({
        id: 1,
        platformId: 1,
        userId: 1,
        resId: 61770,
        kind: ActivityKind.Asset,
        type: ActivityType.AssetCreate,
        typeExp: "created an asset",
        date: new Date(2022, 10, 7, 10, 32),
        msg: "Title details",
        objName: "Title Name",
        assetObjectType: ObjectType.Video,
        assetObjectLayer: ObjectLayer.Title,
        userName: "Lucas Boisbourdin",
        isRead: false,
      })
    );

    // Asset clip create by you
    response.activities.push(
      new DbActivityInfos({
        id: 2,
        platformId: 1,
        userId: 1,
        resId: 0,
        kind: ActivityKind.Asset,
        type: ActivityType.AssetClipCreate,
        typeExp: "created a clip on an asset",
        date: new Date(2022, 10, 7, 10, 36),
        msg: "Clip details",
        objName: "Clip Name",
        assetObjectType: ObjectType.VideoClip,
        assetObjectLayer: ObjectLayer.Clip,
        userName: "Lucas Boisbourdin",
        isRead: false,
      })
    );

    // Job failed by system
    response.activities.push(
      new DbActivityInfos({
        id: 3,
        platformId: 1,
        userId: null,
        resId: 0,
        kind: ActivityKind.Config,
        type: ActivityType.JobFailed,
        typeExp: "failed a job",
        date: new Date(2022, 10, 7, 10, 40),
        msg: "Job detail",
        objName: "Job Name",
        userName: "System",
        isRead: true,
      })
    );

    // Issue on order by system
    response.activities.push(
      new DbActivityInfos({
        id: 4,
        platformId: 1,
        userId: null,
        resId: 0,
        kind: ActivityKind.Order,
        type: ActivityType.OrderIssue,
        typeExp: "found an issue on an order",
        date: new Date(2022, 10, 7, 10, 44),
        msg: "Issue details",
        objName: "Order reference",
        userName: "System",
        isRead: true,
      })
    );

    // Asset disabled by Adrien Corbin
    response.activities.push(
      new DbActivityInfos({
        id: 5,
        platformId: 1,
        userId: 2,
        resId: 0,
        kind: ActivityKind.Asset,
        type: ActivityType.AssetDisable,
        typeExp: "disabled an asset",
        date: new Date(2022, 10, 7, 10, 48),
        msg: "Asset details",
        objName: "Asset Name",
        assetObjectType: ObjectType.Subtitle,
        assetObjectLayer: ObjectLayer.Asset,
        userName: "Adrien Corbin",
        isRead: true,
      })
    );

    // Approval accept by Axel Fiodiere
    response.activities.push(
      new DbActivityInfos({
        id: 6,
        platformId: 1,
        userId: 3,
        resId: 0,
        kind: ActivityKind.Asset,
        type: ActivityType.ApprovalAccept,
        typeExp: "accepted an approval",
        date: new Date(2022, 10, 7, 10, 52),
        msg: "Asset details",
        objName: "Asset Name",
        assetObjectType: ObjectType.Audio,
        assetObjectLayer: ObjectLayer.Asset,
        userName: "Axel Fiodiere",
        isRead: true,
      })
    );

    // Group Modified by Maximilien David
    response.activities.push(
      new DbActivityInfos({
        id: 7,
        platformId: 1,
        userId: 4,
        resId: 0,
        kind: ActivityKind.Config,
        type: ActivityType.GroupModify,
        typeExp: "modified a group",
        date: new Date(2022, 10, 7, 10, 56),
        msg: "Group details",
        objName: "Group Name",
        userName: "Maximilien David",
        isRead: true,
      })
    );

    return response;
  }

  private GetResourceLink(activityKind: ActivityKind, activityType: ActivityType, resId: number): string {
    if (activityKind === ActivityKind.Order || activityKind === ActivityKind.OrderItem) {
      return routePaths.ordersItemDetails.path;
    } else if (activityKind === ActivityKind.Asset) {
      return routePaths.asset.path + "/" + resId;
    } else if (activityKind === ActivityKind.Action) {
      return routePaths.asset.path + "/" + resId;
    } else {
      switch (activityType) {
        case ActivityType.ConfigCustomerCreate:
        case ActivityType.ConfigCustomerDelete:
        case ActivityType.ConfigCustomerModify:
        case ActivityType.ConfigIngestPackCreate:
        case ActivityType.ConfigIngestPackDelete:
        case ActivityType.ConfigIngestPackModify:
        case ActivityType.ConfigIngestPlaceholderCreate:
        case ActivityType.ConfigIngestPlaceholderDelete:
        case ActivityType.ConfigIngestPlaceholderModify:
          return routePaths.ingestPacks.path;

        case ActivityType.GroupCreate:
        case ActivityType.GroupDelete:
        case ActivityType.GroupModify:
          return routePaths.settingsRolesAndPermissions.path;

        case ActivityType.UserCreate:
        case ActivityType.UserDelete:
        case ActivityType.UserDisable:
        case ActivityType.UserModify:
          return routePaths.settingsUsers.path;

        case ActivityType.SpeechToTextReceive:
        case ActivityType.SpeechToTextRequest:
          return routePaths.assetActions.path + "/" + resId;
      }
    }
    return "";
  }

  private GetContextLabelFromObjectType(activityKind: ActivityKind, objectType: ObjectType | null): string | undefined {
    if (activityKind !== ActivityKind.Asset || objectType === null) return undefined;

    return GetAssetObjectReadableType(objectType);
  }

  private GetUserInitialsFromUserName(userName: string): string {
    // TODO : see if rework is needed => maybe use concat ?
    const userNameSplitted = userName.split(" ");
    let initials = "";
    for (let i = 0; i < userNameSplitted.length; i++) {
      initials += userNameSplitted[i][0];
    }
    return initials;
  }

  private GetHeadingActionAndLabelFromTypExp(typExp: string): [string, string] {
    if (typExp === "") return ["", ""];

    // type exp pattern : "word word kind"

    // get label (last word)
    const typExpSplitted = typExp.split(" ");
    const label = typExpSplitted[typExpSplitted.length - 1];
    // get action (all words before label)
    const lastIndexOfWhiteSpace = typExp.lastIndexOf(" ");
    const action = typExp.substring(0, lastIndexOfWhiteSpace + 1);
    return [action, label];
  }

  private GetActivityVariantFromActivityType(activityType: ActivityType, solved: boolean): ActivityVariant {
    if (solved) return "info";

    switch (activityType) {
      case ActivityType.OrderFailed:
      case ActivityType.JobFailed:
      case ActivityType.WorkflowFailed:
        return "error";
      case ActivityType.OrderIssue:
      case ActivityType.JobAbort:
      case ActivityType.WorkflowAbort:
        return "warning";
      case ActivityType.ApprovalDecline:
      case ActivityType.ApprovalDelete:
      case ActivityType.OrderRetry:
      case ActivityType.OrderRetryScratch:
      case ActivityType.AssetDisable:
      case ActivityType.AssetDelete:
      case ActivityType.AssetFileDelete:
      case ActivityType.AssetShareDelete:
      case ActivityType.AssetClipDelete:
      case ActivityType.UserDelete:
      case ActivityType.GroupDelete:
      case ActivityType.WorkflowDelete:
        return "info";
      case ActivityType.OrderCompleted:
      case ActivityType.JobCompleted:
      case ActivityType.WorkflowCompleted:
      case ActivityType.ApprovalAccept:
        return "success";
      case ActivityType.AssetCreate:
      case ActivityType.AssetShareCreate:
      case ActivityType.AssetClipCreate:
      case ActivityType.WorkflowCreate:
      case ActivityType.UserCreate:
      case ActivityType.GroupCreate:
      case ActivityType.ConfigIngestPackCreate:
      case ActivityType.ConfigIngestPlaceholderCreate:
      case ActivityType.ConfigCustomerCreate:
        return "primary";

      default:
        return "secondary";
    }
  }
  //------Utils------
  GetServiceUrl(): string {
    return this.logApiClient.GetApiUrl();
  }
}
